import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, message, Modal, Row, Space, Tag } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import List, { ListRef } from '../../components/Templates/List';
import { UpsertModal, type FormFieldConfig } from '../../components/Templates/UpsertModal';
import type { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';
import { brandApi } from '../../services/manager/brand';
import { lockFeeConfigApi } from '../../services/manager/lock-fee-config';
import type { Brand } from '../../types/entity/brand';
import type { LockFeeConfig } from '../../types/entity/lock-fee-config';
import { LockFeeConfigLabels } from '../../types/entity/lock-fee-config';

const productQualityOptions = [
  { label: '全新', value: 'NEW' },
  { label: '二手', value: 'SECOND_HAND' },
];

const purchaseChannelOptions = [
  { label: '平台', value: 'platform' },
  { label: '自采', value: 'self_purchase' },
];

const statusOptions = [
  { label: '启用', value: 1 },
  { label: '禁用', value: 0 },
];

const LockFeeConfigs: React.FC = () => {
  const listRef = useRef<ListRef>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    record: LockFeeConfig | null;
  }>({
    open: false,
    mode: 'create',
    record: null,
  });

  useEffect(() => {
    const loadBrands = async () => {
      setBrandLoading(true);
      try {
        const res = await brandApi.list({ page: 1, pageSize: 1000 });
        setBrands(res.data.items || []);
      } catch (error: any) {
        message.error(error?.message || '加载品牌列表失败');
      } finally {
        setBrandLoading(false);
      }
    };

    loadBrands();
  }, []);

  const brandMap = useMemo(() => {
    return brands.reduce<Record<string, Brand>>((acc, brand) => {
      acc[String(brand.id)] = brand;
      return acc;
    }, {});
  }, [brands]);

  const brandOptions = useMemo(() => {
    return brands.map((brand) => ({
      label: `${brand.name || '-'}${brand.sourceBrandId ? ` (${brand.sourceBrandId})` : ''}`,
      value: String(brand.id),
    }));
  }, [brands]);

  const queryFields: FieldConfig[] = [
    { key: 'brandId', label: LockFeeConfigLabels.brandId, type: 'select', options: brandOptions },
    { key: 'productQuality', label: LockFeeConfigLabels.productQuality, type: 'select', options: productQualityOptions },
    { key: 'purchaseChannel', label: LockFeeConfigLabels.purchaseChannel, type: 'select', options: purchaseChannelOptions },
    { key: 'status', label: LockFeeConfigLabels.status, type: 'select', options: statusOptions },
  ];

  const formFields: FormFieldConfig[] = [
    {
      name: 'brandId',
      label: LockFeeConfigLabels.brandId,
      type: 'select',
      required: true,
      options: brandOptions,
      fieldProps: {
        showSearch: true,
        optionFilterProp: 'label',
        loading: brandLoading,
      },
    },
    {
      name: 'productQuality',
      label: LockFeeConfigLabels.productQuality,
      type: 'select',
      required: true,
      options: productQualityOptions,
    },
    {
      name: 'purchaseChannel',
      label: LockFeeConfigLabels.purchaseChannel,
      type: 'select',
      required: true,
      options: purchaseChannelOptions,
    },
    {
      name: 'amount',
      label: LockFeeConfigLabels.amount,
      type: 'number',
      required: true,
      fieldProps: { min: 0, precision: 2, step: 0.01 },
    },
    {
      name: 'status',
      label: LockFeeConfigLabels.status,
      type: 'select',
      initialValue: 1,
      options: statusOptions,
    },
  ];

  const columns: ColumnsType<LockFeeConfig> = [
    { title: 'ID', dataIndex: 'id', width: 90, fixed: 'left' },
    {
      title: LockFeeConfigLabels.brandId,
      dataIndex: 'brandId',
      width: 220,
      render: (value: string | number) => renderBrand(value, brandMap),
    },
    {
      title: LockFeeConfigLabels.productQuality,
      dataIndex: 'productQuality',
      width: 120,
      render: (value?: string) => renderProductQuality(value),
    },
    {
      title: LockFeeConfigLabels.purchaseChannel,
      dataIndex: 'purchaseChannel',
      width: 120,
      render: (value?: string) => renderPurchaseChannel(value),
    },
    {
      title: LockFeeConfigLabels.amount,
      dataIndex: 'amount',
      width: 120,
      render: (value?: number) => `¥${Number(value || 0).toFixed(2)}`,
    },
    {
      title: LockFeeConfigLabels.status,
      dataIndex: 'status',
      width: 100,
      render: (value?: number) => (
        <Tag color={value === 1 ? 'green' : 'red'}>
          {value === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      width: 180,
      render: (_, record) => formatDateTime(record.createdAt || record.created_at),
    },
    {
      title: '更新时间',
      width: 180,
      render: (_, record) => formatDateTime(record.updatedAt || record.updated_at),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} size="small" onClick={() => handleView(record, brandMap)}>
            查看
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => setModalState({ open: true, mode: 'edit', record })}
          >
            编辑
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} size="small" onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const handleDelete = (record: LockFeeConfig) => {
    Modal.confirm({
      title: '确认删除上锁费用配置',
      content: '确定删除这条上锁费用配置吗？',
      onOk: async () => {
        try {
          await lockFeeConfigApi.deleteById(String(record.id));
          message.success('删除成功');
          listRef.current?.reload();
        } catch (error: any) {
          message.error(error?.message || '删除失败');
        }
      },
    });
  };

  return (
    <>
      <List<LockFeeConfig>
        ref={listRef}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>上锁费用管理</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ open: true, mode: 'create', record: null })}>
              新增配置
            </Button>
          </div>
        }
        queryFields={queryFields}
        fetcher={async (params) => {
          const res = await lockFeeConfigApi.list(params);
          return { rows: res.data.items, pagination: res.data.pagination };
        }}
        buildParams={buildListParams}
        columns={columns}
        rowKey={(row) => String(row.id)}
        tableProps={{ scroll: { x: 'max-content' } }}
      />

      <UpsertModal<LockFeeConfig>
        title={modalState.mode === 'create' ? '新增上锁费用配置' : '编辑上锁费用配置'}
        open={modalState.open}
        mode={modalState.mode}
        record={modalState.record}
        onCancel={() => setModalState((prev) => ({ ...prev, open: false }))}
        onSuccess={() => {
          setModalState((prev) => ({ ...prev, open: false }));
          listRef.current?.reload();
        }}
        createApi={(data) => lockFeeConfigApi.create(data)}
        updateApi={(id, data) => lockFeeConfigApi.updateById(id, data)}
        formFields={formFields}
        beforeEdit={(record) => ({
          ...record,
          brandId: record.brandId !== undefined && record.brandId !== null ? String(record.brandId) : undefined,
        })}
        beforeSubmit={(values) => ({
          brandId: Number(values.brandId),
          productQuality: values.productQuality,
          purchaseChannel: values.purchaseChannel,
          amount: Number(values.amount || 0),
          status: values.status,
        })}
      />
    </>
  );
};

const buildListParams = (query: ForgeQuery) => {
  const params: Record<string, any> = {
    page: query.page,
    pageSize: query.pageSize,
  };

  if (query.filter) {
    Object.entries(query.filter).forEach(([key, value]) => {
      if (value && typeof value === 'object' && 'value' in value) {
        params[key] = (value as any).value;
      }
    });
  }

  return params;
};

const renderBrand = (brandId: string | number | undefined, brandMap: Record<string, Brand>) => {
  if (brandId === undefined || brandId === null) {
    return '-';
  }
  const brand = brandMap[String(brandId)];
  if (!brand) {
    return String(brandId);
  }
  return (
    <Space direction="vertical" size={0}>
      <span style={{ fontWeight: 600 }}>{brand.name || '-'}</span>
      <span style={{ color: '#8c8c8c', fontSize: 12 }}>
        ID: {brandId}{brand.sourceBrandId ? ` / 来源: ${brand.sourceBrandId}` : ''}
      </span>
    </Space>
  );
};

const renderProductQuality = (value?: string) => {
  const option = productQualityOptions.find((item) => item.value === value);
  return <Tag color={value === 'NEW' ? 'blue' : 'purple'}>{option?.label || value || '-'}</Tag>;
};

const renderPurchaseChannel = (value?: string) => {
  const option = purchaseChannelOptions.find((item) => item.value === value);
  return <Tag color={value === 'platform' ? 'gold' : 'green'}>{option?.label || value || '-'}</Tag>;
};

const handleView = (record: LockFeeConfig, brandMap: Record<string, Brand>) => {
  Modal.info({
    title: '上锁费用配置详情',
    width: 720,
    content: (
      <div style={{ marginTop: 16 }}>
        <DetailRow label="ID" value={String(record.id)} />
        <DetailRow label={LockFeeConfigLabels.brandId} valueNode={renderBrand(record.brandId, brandMap)} />
        <DetailRow label={LockFeeConfigLabels.productQuality} valueNode={renderProductQuality(record.productQuality)} />
        <DetailRow label={LockFeeConfigLabels.purchaseChannel} valueNode={renderPurchaseChannel(record.purchaseChannel)} />
        <DetailRow label={LockFeeConfigLabels.amount} value={`¥${Number(record.amount || 0).toFixed(2)}`} />
        <DetailRow
          label={LockFeeConfigLabels.status}
          valueNode={<Tag color={record.status === 1 ? 'green' : 'red'}>{record.status === 1 ? '启用' : '禁用'}</Tag>}
        />
        <DetailRow label="创建时间" value={formatDateTime(record.createdAt || record.created_at)} />
        <DetailRow label="更新时间" value={formatDateTime(record.updatedAt || record.updated_at)} />
      </div>
    ),
  });
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN');
};

const DetailRow: React.FC<{ label: string; value?: string; valueNode?: React.ReactNode }> = ({ label, value, valueNode }) => (
  <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
    <Col span={7} style={{ fontWeight: 600 }}>{label}:</Col>
    <Col span={17}>{valueNode ?? value ?? '-'}</Col>
  </Row>
);

export default LockFeeConfigs;
